/**
 * A small Markdown renderer that produces React elements.
 *
 * **It never uses `dangerouslySetInnerHTML`, and it never parses HTML.** That is the
 * whole reason it exists rather than a dependency. Body copy on this site is written in
 * the back office by whoever holds an Editor account, and a renderer that passes HTML
 * through turns "someone can edit the About page" into "someone can run script on every
 * visitor's browser, including the next administrator to sign in". React escapes every
 * string it renders as a child, so building elements — rather than markup — makes that
 * class of bug unreachable rather than merely unlikely.
 *
 * The supported subset is deliberately small and covers what the content actually
 * needs: headings, paragraphs, bullet and numbered lists, blockquotes, bold, italic,
 * inline code, and links. Anything else renders as the literal text that was typed,
 * which is the honest failure — the author sees their asterisks and fixes them, rather
 * than the paragraph silently vanishing.
 */

import { Fragment, type ReactNode } from 'react';

/* -------------------------------------------------------------------------- */
/* Inline                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Only http, https and mailto links are rendered as links.
 *
 * `javascript:` is the obvious one to block, but `data:` matters just as much — a
 * `data:text/html` URL executes in the origin it is opened from. Anything else becomes
 * plain text, so the reader still sees what was written.
 */
function safeHref(href: string): string | null {
  const trimmed = href.trim();
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) return trimmed;
  // A site-relative path is fine and is how internal links are written.
  if (/^\/[^/]/.test(trimmed) || trimmed === '/') return trimmed;
  return null;
}

/**
 * Inline formatting, applied in one pass.
 *
 * The order in this alternation is load-bearing: `**bold**` must be tried before
 * `*italic*`, or the italic rule consumes the first asterisk of a bold marker and the
 * rest of the line renders wrong. Code is first so that backticked content is never
 * re-scanned for other markers — `` `**not bold**` `` should print asterisks.
 */
const INLINE = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(\[[^\]]+\]\([^)]+\))/g;

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let index = 0;

  INLINE.lastIndex = 0;
  while ((match = INLINE.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    const token = match[0];
    const key = `${keyPrefix}-${index++}`;

    if (token.startsWith('`')) {
      nodes.push(<code key={key}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith('**')) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('*')) {
      nodes.push(<em key={key}>{token.slice(1, -1)}</em>);
    } else {
      const split = token.indexOf('](');
      const label = token.slice(1, split);
      const href = safeHref(token.slice(split + 2, -1));
      nodes.push(
        href ? (
          <a key={key} href={href} {...(href.startsWith('http') ? { target: '_blank', rel: 'noreferrer noopener' } : {})}>
            {label}
          </a>
        ) : (
          // A refused scheme renders as the text that was typed, brackets and all.
          <Fragment key={key}>{token}</Fragment>
        ),
      );
    }
    lastIndex = match.index + token.length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

/* -------------------------------------------------------------------------- */
/* Block                                                                       */
/* -------------------------------------------------------------------------- */

export function Markdown({ children, className = 'prose' }: { children: string | null | undefined; className?: string }) {
  if (!children) return null;

  const blocks: ReactNode[] = [];
  // Blank line separates blocks. `\r\n` is normalised because content pasted from Word
  // on Windows arrives with carriage returns and would otherwise never match.
  const chunks = children.replace(/\r\n/g, '\n').split(/\n{2,}/);

  chunks.forEach((chunk, blockIndex) => {
    const text = chunk.trim();
    if (!text) return;
    const key = `b${blockIndex}`;

    const heading = /^(#{2,4})\s+(.*)$/.exec(text);
    if (heading) {
      const level = heading[1]!.length;
      const content = renderInline(heading[2]!, key);
      blocks.push(level === 2 ? <h2 key={key}>{content}</h2> : level === 3 ? <h3 key={key}>{content}</h3> : <h4 key={key}>{content}</h4>);
      return;
    }

    if (text.startsWith('> ')) {
      blocks.push(
        <blockquote key={key}>
          {renderInline(text.split('\n').map((line) => line.replace(/^>\s?/, '')).join(' '), key)}
        </blockquote>,
      );
      return;
    }

    const lines = text.split('\n');

    if (lines.every((line) => /^\s*[-*+]\s+/.test(line))) {
      blocks.push(
        <ul key={key}>
          {lines.map((line, i) => (
            <li key={`${key}-${i}`}>
              <span>{renderInline(line.replace(/^\s*[-*+]\s+/, ''), `${key}-${i}`)}</span>
            </li>
          ))}
        </ul>,
      );
      return;
    }

    if (lines.every((line) => /^\s*\d+[.)]\s+/.test(line))) {
      blocks.push(
        <ol key={key} style={{ listStyle: 'decimal', paddingLeft: '1.4em', display: 'grid', gap: 8 }}>
          {lines.map((line, i) => (
            <li key={`${key}-${i}`} style={{ display: 'list-item' }}>
              {renderInline(line.replace(/^\s*\d+[.)]\s+/, ''), `${key}-${i}`)}
            </li>
          ))}
        </ol>,
      );
      return;
    }

    // A single newline inside a paragraph is a line break, matching what someone
    // typing into a textarea expects to see.
    blocks.push(
      <p key={key}>
        {lines.map((line, i) => (
          <Fragment key={`${key}-l${i}`}>
            {i > 0 ? <br /> : null}
            {renderInline(line, `${key}-l${i}`)}
          </Fragment>
        ))}
      </p>,
    );
  });

  return <div className={className}>{blocks}</div>;
}

/** Strip formatting for a meta description or a card excerpt. */
export function plainText(markdown: string | null | undefined, limit = 200): string {
  if (!markdown) return '';
  const flat = markdown
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^>\s?/gm, '')
    .replace(/\s+/g, ' ')
    .trim();
  return flat.length > limit ? `${flat.slice(0, limit - 1).trimEnd()}…` : flat;
}
